import { useState } from "react";
import axios from "axios";
import { getApiUrl } from "../../../../../config/api";
import ConfirmModal from "../../../../../components/ConfirmModal";
import { t } from "../../shared/trendReportShared";
import AudiencePanel from "./AudiencePanel";
import AudiencePicker from "./AudiencePicker";
import SendPanel from "./SendPanel";
import DispatchHistory from "./DispatchHistory";

// Orchestrator for the Daily Email Report tab. All server state is owned by
// TrendReportsFeedPage and arrives as props; everything here is either a
// mutation or transient UI state.
//
// The model, in one line: group audiences say who *should* receive the brief,
// and their members are resolved into individual subscribers before each send.
// AudiencePanel shows both as one nested list so the same person is never
// counted twice on screen. See
// backend/domains/trend_reports/news_letter/subscribers.py.
export default function NewsletterTab({
  language,
  currentUser,
  subscribers,
  dispatches,
  audiences,
  canSend,
  canManageAudience,
  onChange,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState("");
  const [busyGroup, setBusyGroup] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const base = `${getApiUrl()}/api/v2/trend-reports`;

  const authHeaders = async () => {
    const token = await currentUser.getIdToken();
    return { Authorization: `Bearer ${token}` };
  };

  const flash = (message) => {
    setNotice(message);
    setTimeout(() => setNotice(""), 4000);
  };

  // ── audiences ──────────────────────────────────────────────────────────────

  const openPicker = () => setPickerOpen(true);

  // Both return an error string on failure and undefined on success. `refresh`
  // is off during a batch so the list is refetched once at the end rather than
  // after every row.
  const addGroup = async (groupId, lang, { refresh = true } = {}) => {
    try {
      const res = await axios.post(
        `${base}/audiences`,
        { group_id: groupId, language: lang },
        { headers: await authHeaders() },
      );
      if (!res.data?.success)
        return res.data?.error || t("newsletterAddError", language);
      if (refresh) await onChange();
    } catch (e) {
      return e.response?.data?.error || t("newsletterAddError", language);
    }
  };

  const addPerson = async (payload, { refresh = true } = {}) => {
    try {
      const res = await axios.post(`${base}/subscribers`, payload, {
        headers: await authHeaders(),
      });
      if (!res.data?.success)
        return res.data?.error || t("newsletterAddError", language);
      if (refresh) await onChange();
    } catch (e) {
      return e.response?.data?.error || t("newsletterAddError", language);
    }
  };

  // The picker collects a whole selection before committing, so writes go out
  // one at a time - Firestore, and one bad row must not abort the rest - and
  // every failure is reported back with the entry it belongs to.
  const addMany = async (entries, onProgress) => {
    const failed = [];
    let added = 0;
    for (let i = 0; i < entries.length; i += 1) {
      const entry = entries[i];
      const message =
        entry.kind === "group"
          ? await addGroup(entry.groupId, entry.language, { refresh: false })
          : await addPerson(
              { ...entry.payload, language: entry.language },
              { refresh: false },
            );
      if (message)
        failed.push({ key: entry.key, label: entry.label, error: message });
      else added += 1;
      onProgress?.(i + 1, entries.length);
    }
    await onChange();
    return { added, failed };
  };

  const setGroupLanguage = async (groupId, lang) => {
    setBusyGroup(groupId);
    try {
      await axios.patch(
        `${base}/audiences/${encodeURIComponent(groupId)}`,
        { language: lang },
        { headers: await authHeaders() },
      );
      await onChange();
    } catch (e) {
      setError(e.response?.data?.error || t("newsletterAddError", language));
    } finally {
      setBusyGroup("");
    }
  };

  const removeGroup = (groupId) =>
    setConfirm({
      title: t("newsletterAudiences", language),
      message: t("newsletterRemoveAudience", language),
      onConfirm: async () => {
        setConfirm(null);
        setBusyGroup(groupId);
        try {
          await axios.delete(
            `${base}/audiences/${encodeURIComponent(groupId)}`,
            {
              headers: await authHeaders(),
            },
          );
          await onChange();
        } catch (e) {
          setError(
            e.response?.data?.error || t("newsletterAddError", language),
          );
        } finally {
          setBusyGroup("");
        }
      },
    });

  const syncNow = async () => {
    setSyncing(true);
    setError("");
    try {
      const res = await axios.post(
        `${base}/audiences/sync`,
        {},
        { headers: await authHeaders() },
      );
      if (res.data?.success) {
        flash(t("newsletterSynced", language));
        await onChange();
      } else {
        setError(res.data?.error || t("newsletterSyncError", language));
      }
    } catch (e) {
      setError(e.response?.data?.error || t("newsletterSyncError", language));
    } finally {
      setSyncing(false);
    }
  };

  // ── individual recipients ──────────────────────────────────────────────────
  // Editing one person clears their source_group server-side, pinning them so a
  // later group sync leaves the override alone.

  const setSubscriberLanguage = async (sub, lang) => {
    setBusyId(sub.id);
    try {
      await axios.patch(
        `${base}/subscribers/${sub.id}`,
        { language: lang },
        { headers: await authHeaders() },
      );
      await onChange();
    } catch (e) {
      setError(e.response?.data?.error || t("newsletterAddError", language));
    } finally {
      setBusyId(null);
    }
  };

  const removeSubscriber = async (sub) => {
    setBusyId(sub.id);
    try {
      await axios.delete(`${base}/subscribers/${sub.id}`, {
        headers: await authHeaders(),
      });
      await onChange();
    } catch (e) {
      setError(e.response?.data?.error || t("newsletterAddError", language));
    } finally {
      setBusyId(null);
    }
  };

  // ── sending ────────────────────────────────────────────────────────────────

  const handleSendTest = async () => {
    setBusy("test");
    setError("");
    try {
      const res = await axios.post(
        `${base}/dispatch/test`,
        {},
        { headers: await authHeaders() },
      );
      if (res.data?.success) {
        flash(`${t("newsletterTestSent", language)} ${res.data.sent_to || ""}`);
        await onChange();
      } else {
        setError(res.data?.error || t("newsletterSendError", language));
      }
    } catch (e) {
      setError(e.response?.data?.error || t("newsletterSendError", language));
    } finally {
      setBusy("");
    }
  };

  const handleSendNow = () =>
    setConfirm({
      title: t("newsletterSendNow", language),
      message: t("newsletterSendConfirm", language),
      variant: "warning",
      confirmLabel: t("newsletterSendNow", language),
      onConfirm: async () => {
        setConfirm(null);
        setBusy("send");
        setError("");
        try {
          const res = await axios.post(
            `${base}/dispatch`,
            {},
            { headers: await authHeaders() },
          );
          if (res.data?.success) {
            flash(
              res.data.skipped
                ? res.data.reason
                : `${res.data.sent_count}/${res.data.recipient_count} ${t("newsletterEmailsCount", language)}`,
            );
            await onChange();
          } else {
            setError(res.data?.error || t("newsletterSendError", language));
          }
        } catch (e) {
          setError(
            e.response?.data?.error || t("newsletterSendError", language),
          );
        } finally {
          setBusy("");
        }
      },
    });

  return (
    <div className="animate-fadeIn max-w-3xl mx-auto flex flex-col gap-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-40 h-40 bg-orange-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-orange-500/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        <div className="relative text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-500/10 rounded-2xl mb-4">
            <svg
              className="w-8 h-8 text-orange-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {t("newsletterTitle", language)}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            {t("newsletterHint", language)}
          </p>
        </div>
      </div>

      {canManageAudience && (
      <AudiencePanel
        language={language}
        audiences={audiences}
        subscribers={subscribers}
        syncing={syncing}
        busyGroup={busyGroup}
        busyId={busyId}
        onAddClick={openPicker}
        onSetGroupLanguage={setGroupLanguage}
        onRemoveGroup={removeGroup}
        onSetSubscriberLanguage={setSubscriberLanguage}
        onRemoveSubscriber={removeSubscriber}
        onSync={syncNow}
      />
      )}

      {canSend && (
        <SendPanel
          language={language}
          busy={busy}
          error={error}
          notice={notice}
          onSendTest={handleSendTest}
          onSendNow={handleSendNow}
        />
      )}

      {canSend && <DispatchHistory language={language} dispatches={dispatches} />}

      {pickerOpen && (
        <AudiencePicker
          language={language}
          audiences={audiences}
          subscribers={subscribers}
          onAddMany={addMany}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          variant={confirm.variant || "danger"}
          confirmLabel={confirm.confirmLabel || t("newsletterAdd", language)}
          cancelLabel={t("newsletterClose", language)}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
